import React from 'react';
import {
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { Box, Button, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import {
  CmdbIngestionIssue,
  CmdbIngestionIssueKind,
  useCmdbIngestionReport,
} from '../../hooks';

/**
 * What each finding is called in the table. The backend names the kinds after
 * what went wrong, the table says it the way someone looking for a missing
 * team would put it.
 */
const KIND_TITLES: Record<CmdbIngestionIssueKind, string> = {
  'nameless-entity': 'No identifier',
  'duplicate-entity': 'Duplicate entity ref',
  'unresolved-team': 'Team not in cmdb.teams',
  'dangling-cluster-slice': 'Cluster slice without its cluster',
};

const columns: TableColumn<CmdbIngestionIssue>[] = [
  {
    title: 'Finding',
    field: 'kind',
    width: '20%',
    render: issue => KIND_TITLES[issue.kind] ?? issue.kind,
  },
  { title: 'What happened', field: 'message' },
  {
    title: 'Affected',
    field: 'refs',
    width: '10%',
    type: 'numeric',
    render: issue => issue.refs.length,
  },
];

/** The lost objects of one finding, listed one per line. */
function IssueDetail({ rowData }: { rowData: CmdbIngestionIssue }) {
  return (
    <Box padding={2}>
      {rowData.refs.map((ref, index) => (
        // The same string can stand for two objects that were lost the same
        // way, so the position is what tells the rows apart.
        <Typography key={`${ref}-${index}`} variant="body2">
          {ref}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * What the CMDB export loses before the catalog ever sees it.
 *
 * The provider drops what it cannot turn into an entity - an object without an
 * identifier, a second object claiming a ref that is taken - and a dropped
 * object leaves no trace in the catalog: there is no entity to carry a
 * processing error, so the Unprocessed Entities tab has nothing to show for it
 * either. This tab asks the backend to map the current export once more and
 * report what falls out.
 */
export const CmdbIngestionContent = () => {
  const { report, loading, error, retry } = useCmdbIngestionReport();

  if (loading && !report) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  if (!report) {
    return <Alert severity="info">No CMDB provider is configured</Alert>;
  }

  const lost = report.issues.reduce(
    (total, issue) => total + issue.refs.length,
    0,
  );

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginBottom={2}
      >
        <Box>
          <Typography variant="body2">
            {`${report.entityCount} entities from the CMDB export written ${report.exportLastModified}`}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {`Read ${report.checkedAt} by ${report.provider}. The export is judged as it is now, which is not necessarily the one the last refresh ran on.`}
          </Typography>
        </Box>
        {/* The backend hands the same report out again for a minute, so this
            re-reads the export only once that has passed. */}
        <Button variant="outlined" onClick={retry} disabled={loading}>
          Check again
        </Button>
      </Box>
      {/* Without findings the table would be a header over nothing. */}
      {report.issues.length === 0 ? (
        <Alert severity="success">
          The CMDB export maps cleanly, nothing was dropped
        </Alert>
      ) : (
        <Table<CmdbIngestionIssue>
          title={`Ingestion findings (${lost} objects)`}
          options={{ search: false, paging: false, padding: 'dense' }}
          columns={columns}
          data={report.issues}
          detailPanel={IssueDetail}
        />
      )}
    </>
  );
};
